<script setup lang="ts">
import { EButton, ETooltip } from '@erpx/ui'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute } from 'vue-router'

import AppIcon from '@/components/AppIcon.vue'
import AppSidebarRow from '@/components/AppSidebarRow.vue'
import AppTile from '@/components/AppTile.vue'
import {
  SIDENAV_ID,
  activeNavItem,
  filterSection,
  foldForSearch,
  useCurrentApp,
  useNavPrefs,
} from '@/composables/useNavPrefs'
import { useOverlays } from '@/composables/useOverlays'
import { useUserScope } from '@/composables/useUserScope'
import { appItems, appLandingPath } from '@/nav/apps'
import type { NavGroup } from '@/nav/items'
import { useAuthStore } from '@/stores/auth'

/**
 * The left navigation — for the current app only.
 *
 * It used to list every screen in the product under eighteen headings, which
 * was complete and unusable. Now it is one app's menu (one FSD part, or Home,
 * or Me), headed by that app's tile so the user always knows which part of
 * the product they are in; the launcher in the top bar is how they move
 * between apps.
 *
 * What §11 asks of it is kept: it collapses to icons for people who live in
 * the grid, and favourites put a warehouse officer one click from the one
 * screen they use all day — shown here as "Pinned" when they belong to this
 * app. Recents moved to the home screen, which is where "what was I doing"
 * gets asked.
 *
 * Parts with several sections get collapsible section headers — the FSD's
 * own sections, each headed by its FSD number, short title and screen count,
 * with "Expand all / Collapse all" above them. A long part (Finance) opens to
 * the section the user is working in rather than to seventy rows. A part
 * with one section is a plain list: a heading over a single group would only
 * repeat the app's name. The filter matches FSD numbers, feature codes and
 * section titles as well as screen labels.
 *
 * What it never does is render a destination the user cannot reach —
 * `visibleApps` filters on the permission set from `/auth/me` (CLAUDE.md §6).
 */

/** Above this many items an app's menu gets a filter field. */
const FILTER_THRESHOLD = 12
/**
 * Parts with more sections than this open with only the active section
 * expanded; smaller ones open fully. Three sections fit on a laptop screen
 * expanded; Finance's nine do not.
 */
const EXPAND_ALL_UP_TO = 3
/**
 * …unless the sections are small. Since C17 a part is cut into the FSD's own
 * sections, so Enterprise has a dozen sections of one to fourteen screens each
 * (about sixty rows in all since R0's menu parity). Folding those away leaves
 * a column of headers the user must open one by one to find anything; the
 * reference FSD menu opens such a part fully, and so do we. Up to this many
 * screens in the whole part, every section opens by default. Finance (~seventy
 * rows) stays above it and keeps opening to the active section only.
 */
const EXPAND_ALL_ITEMS_UP_TO = 64
/** The breakpoint at which the sidebar becomes a drawer (styles.css matches it). */
const NARROW_QUERY = '(max-width: 768px)'

const auth = useAuthStore()
const route = useRoute()
const overlays = useOverlays()
const { t } = useI18n()

const userScope = useUserScope()
const prefs = useNavPrefs(userScope, auth.can)
const { current } = useCurrentApp(userScope, auth.can)

const navEl = ref<HTMLElement | null>(null)
const filterId = useId()

/* --------------------------------------------------- narrow-screen drawer */

const narrow = ref(false)
let media: MediaQueryList | null = null
const onMediaChange = (event: MediaQueryListEvent): void => {
  narrow.value = event.matches
}

onMounted(() => {
  if (typeof window.matchMedia !== 'function') return
  media = window.matchMedia(NARROW_QUERY)
  narrow.value = media.matches
  media.addEventListener('change', onMediaChange)
})

onBeforeUnmount(() => {
  media?.removeEventListener('change', onMediaChange)
})

// In the drawer the sidebar always shows labels: an icon rail sliding in over
// the page would be the worst of both.
const rail = computed(() => prefs.collapsed.value && !narrow.value)

watch(
  () => prefs.drawerOpen.value,
  (isOpen) => {
    if (!isOpen || !narrow.value) return
    void nextTick(() => navEl.value?.querySelector<HTMLElement>('a[href], button, input')?.focus())
  },
)

/* -------------------------------------------------------------- the app */

const app = computed(() => current.value?.app)
const part = computed(() => current.value?.part)
const appName = computed(() => (app.value === undefined ? '' : t(app.value.labelKey)))
const landing = computed(() => (part.value === undefined ? '/' : appLandingPath(part.value)))
const items = computed(() => (part.value === undefined ? [] : appItems(part.value)))
const activeId = computed(() => activeNavItem(items.value, route.path)?.id)
const multiSection = computed(() => (part.value?.sections.length ?? 0) > 1)

/**
 * The app's tint drives the active-row accent. `to` is the deep stop — dark
 * enough against white for a 3:1 indicator — and `from` the light one, which
 * is what reads against the dark theme's surfaces; styles.css picks between
 * them on `.dark`.
 */
const tintStyle = computed(() =>
  app.value === undefined
    ? undefined
    : { '--app-tint-light': app.value.tint.to, '--app-tint-dark': app.value.tint.from },
)

/* ------------------------------------------------------------- filtering */

const query = ref('')
const showFilter = computed(() => !rail.value && items.value.length > FILTER_THRESHOLD)
// A query left over in a field that is no longer on screen (the user
// collapsed to the rail mid-filter) must not keep hiding rows.
const folded = computed(() => (showFilter.value ? foldForSearch(query.value) : ''))
const filtering = computed(() => folded.value !== '')

// A filter typed in Finance means nothing in Inventory.
watch(
  () => app.value?.id,
  () => {
    query.value = ''
  },
)

const sections = computed<NavGroup[]>(() => {
  const all = part.value?.sections ?? []
  if (!filtering.value) return all
  // A query matches a section by FSD number, feature code or title (all its
  // screens stay), or a screen by its label, keywords, number or code.
  return all
    .map((section) => ({ ...section, items: filterSection(section, folded.value) }))
    .filter((section) => section.items.length > 0)
})

/** A one-section part renders as a plain list. */
const singleItems = computed(() => (multiSection.value ? [] : (sections.value[0]?.items ?? [])))

const noMatches = computed(() => filtering.value && sections.value.length === 0)

/** Favourites that belong to this app, in the order they were starred. */
const pinned = computed(() => {
  if (filtering.value) return []
  const ids = new Set(items.value.map((item) => item.id))
  return prefs.favourites.value.filter((item) => ids.has(item.id))
})

function onFilterKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || query.value === '') return
  // First Escape clears the filter; only an empty field lets Escape through
  // to close the drawer.
  event.stopPropagation()
  query.value = ''
}

/* -------------------------------------------------------------- sections */

const activeSectionId = computed(
  () => part.value?.sections.find((section) => section.items.some((item) => item.id === activeId.value))?.id,
)

// The section holding the screen the user is on is open on arrival, whatever
// they last left it as — arriving at a screen whose own menu entry is folded
// away would hide where they are.
watch(
  [() => app.value?.id, activeSectionId],
  ([appId, sectionId]) => {
    if (appId !== undefined && sectionId !== undefined) prefs.setSectionOpen(appId, sectionId, true)
  },
  { immediate: true },
)

function isOpen(section: NavGroup): boolean {
  // A filter shows every section with a match, expanded: hiding a match
  // behind a fold is a filter that did not finish its job.
  if (filtering.value || app.value === undefined) return true
  return prefs.isSectionOpen(app.value.id, section.id, openByDefault.value)
}

/** What a section the user has never toggled shows — see the two constants above. */
const openByDefault = computed(
  () =>
    (part.value?.sections.length ?? 0) <= EXPAND_ALL_UP_TO ||
    items.value.length <= EXPAND_ALL_ITEMS_UP_TO,
)

function toggleSection(section: NavGroup): void {
  if (app.value === undefined) return
  prefs.setSectionOpen(app.value.id, section.id, !isOpen(section))
}

/* -------------------------------------------- expand all / collapse all */

// Hidden while filtering: a filter already expands every match, and the
// filtered subset is not what "all" should mean.
const showTools = computed(() => multiSection.value && !rail.value && !filtering.value)
const allOpen = computed(() => sections.value.length > 0 && sections.value.every((section) => isOpen(section)))

function toggleAllSections(): void {
  if (app.value === undefined) return
  prefs.setSectionsOpen(
    app.value.id,
    sections.value.map((section) => section.id),
    !allOpen.value,
  )
}

/** Hover title: FSD number, full FSD title and feature code; label only when unnumbered. */
function sectionTitle(section: NavGroup): string {
  const label = t(section.labelKey)
  if (section.fsd === undefined) return label
  const full = section.fullTitleKey === undefined ? label : t(section.fullTitleKey)
  const code = section.code === undefined ? '' : ` · ${section.code}`
  return `${section.fsd} ${full}${code}`
}

const sectionListId = (section: NavGroup): string => `${filterId}-section-${section.id}`
</script>

<template>
  <nav
    v-if="app"
    :id="SIDENAV_ID"
    ref="navEl"
    class="sidenav"
    :class="{ 'sidenav--collapsed': rail, 'sidenav--open': prefs.drawerOpen.value }"
    :style="tintStyle"
    :aria-label="appName"
  >
    <div class="sidenav__head">
      <ETooltip :content="appName" side="right" :disabled="!rail">
        <RouterLink class="sidenav__app" :to="landing">
          <AppTile :app="app" :size="rail ? 28 : 32" />
          <span :class="rail ? 'sr-only' : 'sidenav__app-text'">
            <span class="sidenav__app-name">{{ appName }}</span>
            <span v-if="app.fsdPart !== null && !rail" class="sidenav__app-caption">
              {{ t('shell.sidebar.fsdPart', { n: app.fsdPart }) }}
            </span>
          </span>
        </RouterLink>
      </ETooltip>
    </div>

    <div v-if="showFilter" class="sidenav__filter">
      <AppIcon name="search" :size="13" class="sidenav__filter-icon" />
      <input
        :id="filterId"
        v-model="query"
        type="search"
        class="sidenav__filter-input"
        autocomplete="off"
        spellcheck="false"
        :placeholder="t('shell.sidebar.filterPlaceholder')"
        :aria-label="t('shell.sidebar.filterLabel', { app: appName })"
        @keydown="onFilterKeydown"
      />
    </div>

    <div class="sidenav__scroll">
      <div v-if="pinned.length > 0 && !rail" class="sidenav__group">
        <p class="sidenav__group-label">{{ t('shell.sidebar.pinned') }}</p>
        <ul class="sidenav__list">
          <AppSidebarRow
            v-for="item in pinned"
            :key="`pin-${item.id}`"
            :item="item"
            :active="item.id === activeId"
            :rail="false"
            :favourite="true"
            @toggle-favourite="prefs.toggleFavourite"
          />
        </ul>
      </div>

      <!-- One section: a plain list. -->
      <ul v-if="!multiSection && singleItems.length > 0" class="sidenav__list">
        <AppSidebarRow
          v-for="item in singleItems"
          :key="item.id"
          :item="item"
          :active="item.id === activeId"
          :rail="rail"
          :favourite="prefs.isFavourite(item.id)"
          @toggle-favourite="prefs.toggleFavourite"
        />
      </ul>

      <!-- Several sections: collapsible headers (a divider each in the rail). -->
      <template v-else-if="multiSection">
        <div v-if="showTools" class="sidenav__tools">
          <button type="button" class="sidenav__tool" @click="toggleAllSections">
            {{ allOpen ? t('shell.sidebar.collapseAll') : t('shell.sidebar.expandAll') }}
          </button>
        </div>
        <div v-for="section in sections" :key="section.id" class="sidenav__section">
          <button
            v-if="!rail"
            type="button"
            class="sidenav__section-toggle"
            :aria-expanded="isOpen(section)"
            :aria-controls="sectionListId(section)"
            :title="sectionTitle(section)"
            @click="toggleSection(section)"
          >
            <AppIcon
              name="chevronDown"
              :size="12"
              class="sidenav__chevron"
              :class="{ 'is-closed': !isOpen(section) }"
            />
            <span v-if="section.fsd !== undefined" class="sidenav__section-num">{{ section.fsd }}</span>
            <span class="sidenav__section-label">{{ t(section.labelKey) }}</span>
            <!-- Always shown, open or folded: how big a section is, at a glance. -->
            <span class="sidenav__section-count" aria-hidden="true">{{ section.items.length }}</span>
            <span class="sr-only">{{
              t('shell.sidebar.sectionCount', { n: section.items.length }, section.items.length)
            }}</span>
          </button>

          <ul v-show="rail || isOpen(section)" :id="sectionListId(section)" class="sidenav__list">
            <AppSidebarRow
              v-for="item in section.items"
              :key="item.id"
              :item="item"
              :active="item.id === activeId"
              :rail="rail"
              :favourite="prefs.isFavourite(item.id)"
              @toggle-favourite="prefs.toggleFavourite"
            />
          </ul>
        </div>
      </template>

      <div v-if="noMatches" class="sidenav__empty" role="status">
        <p class="sidenav__empty-title">{{ t('shell.sidebar.noMatches', { query: query.trim() }) }}</p>
        <p class="sidenav__empty-text">{{ t('shell.sidebar.noMatchesHint', { app: appName }) }}</p>
        <EButton variant="secondary" size="sm" @click="overlays.openPalette()">
          {{ t('shell.sidebar.searchEverything') }}
        </EButton>
      </div>
    </div>

    <div class="sidenav__footer">
      <EButton
        variant="ghost"
        size="sm"
        block
        :aria-label="rail ? t('shell.sidebar.expandNav') : t('shell.sidebar.collapseNav')"
        @click="prefs.toggleCollapsed()"
      >
        <template #icon>
          <AppIcon :name="rail ? 'chevronRight' : 'chevronLeft'" :size="14" />
        </template>
        <span v-if="!rail">{{ t('shell.sidebar.collapse') }}</span>
      </EButton>
    </div>
  </nav>
</template>
